/**
 * safeStorage — a drop-in replacement for localStorage that never throws.
 *
 * Safari private mode and some iOS WebViews either block localStorage entirely
 * or throw a SecurityError when you try to read/write it.  This utility wraps
 * every call in a try/catch and falls back to an in-memory Map so the app
 * keeps working — the session just won't survive a page refresh in those
 * restricted environments, which is the correct behaviour.
 */

const memoryStore = new Map<string, string>();

function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__vc_ls_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// Detect once at module load — avoids repeated try/catch on every call.
const storageAvailable = isLocalStorageAvailable();

export const safeStorage = {
  getItem(key: string): string | null {
    if (storageAvailable) {
      try {
        return localStorage.getItem(key);
      } catch {
        // fall through to memory store
      }
    }
    return memoryStore.get(key) ?? null;
  },

  setItem(key: string, value: string): void {
    if (storageAvailable) {
      try {
        localStorage.setItem(key, value);
        return;
      } catch {
        // fall through to memory store
      }
    }
    memoryStore.set(key, value);
  },

  removeItem(key: string): void {
    if (storageAvailable) {
      try {
        localStorage.removeItem(key);
      } catch {
        // fall through
      }
    }
    memoryStore.delete(key);
  },
};
